// =============================================================================
// PODS SWITCHER — Horizon `Component` port of the PLP collection-switcher dropdown
// (blocks/pods-switcher.liquid). Wave-1 driver-swap: reparents the behaviour of the
// bundled src/js/objects/global/pods-switcher.js onto Horizon's `Component` base class, loaded as
// native ESM via the `@theme/*` importmap (snippets/horizon-importmap.liquid) rather than the
// webpack src/js bundle.
//
// NOT a zero-change swap (re-labelled 2026-07-05): this wave ships TOGETHER with the 2026-07-03
// content/spec rewrite in blocks/pods-switcher.liquid (close row <button> → <a href aria-current>,
// pod-metaobject field remap name/icon/collection + current-collection filtering, widened mock
// fallback + new mock labels, schema copy) and with two DECLARED visual fixes in
// src/scss/objects/global/_pods-switcher.scss (open-state cross-section z-lift; mobile viewport
// clamp + label ellipsis). Reviewers must baseline against those declared changes, not HEAD parity.
// The driver logic below is a like-for-like port of the old module EXCEPT the Space-key close
// handler — added because the close row is now an <a>, which (unlike the old <button>) does not
// activate on Space; the handler restores button-equivalent keyboard dismissal:
//
//   1. TOGGLE  — the closed ghost pill (ref="toggle") flips the dropdown. setOpen keeps the exact
//                old order: `is-open` class on this root → aria-expanded on the toggle →
//                panel.hidden. Panel visibility is CSS-owned (`.pods-switcher.is-open
//                .pods-switcher__panel` in src/scss/objects/global/_pods-switcher.scss); `hidden`
//                flips in lockstep to cover pre-CSS/no-CSS/AT.
//   2. CLOSE   — the panel's first row (ref="close", the current collection's own link) collapses
//                the panel. preventDefault is UNCONDITIONAL — it swallows cmd/ctrl-click on that
//                row too, exactly like the old module (parity beats fix; middle-click fires
//                auxclick, unaffected).
//   3. DISMISS — outside click (document 'click', BUBBLE phase — capture would fire before the
//                toggle's own handler and self-close on every toggle click) + Escape/Esc, which
//                closes AND returns focus to the toggle. The old module's leaked global document
//                listeners become per-instance listeners: bound in connectedCallback via an
//                AbortController, aborted in disconnectedCallback. Per-instance dismissal
//                reproduces the old multi-root semantics exactly — each instance closes itself
//                when a click lands outside it.
//
// STATE CLASS: `is-open` is KEPT (no rename). Every consumer is ours (_pods-switcher.scss:145
// panel display:flex + :158 the body:has() cross-section z-lift to 40 that the header z-ladder in
// _header.scss documents), so a lockstep rename is PERMITTED — but this wave is build-serialized:
// the shipped assets/global.autostyle.css keeps the `.pods-switcher.is-open` rules until the later
// single build, so shipping a renamed class first would render the dropdown dead-on-click
// mid-wave. No collision to escape, no decoupling need — keep the name.
//
// NO requiredRefs — the old module silently returned when toggle/panel were missing (MissingRefError
// would throw). A malformed root stays permanently inert, mirroring the old
// dataset.podsSwitcherReady guard that was set BEFORE the existence check.
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts — same effective timing
// as the old deferred-bundle boot. Pure DOM + standard events: no IntersectionObserver, no
// matchMedia, no Motto load-order global. Theme Editor re-render replaces the node, so the
// connected/disconnected lifecycle replaces both the old DOMContentLoaded boot and the
// shopify:section:load re-scan.
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify it exists
// before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// Open-state class on the root — CSS owns the panel show/hide + the cross-section z-lift keyed on it.
const OPEN_CLASS = 'is-open';

/**
 * The PLP pods-switcher dropdown custom element (`<pods-switcher class="pods-switcher">`).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [toggle] - the closed ghost pill (button.pods-switcher__toggle).
 * @property {HTMLElement} [panel] - the dropdown panel (div.pods-switcher__panel, starts `hidden`).
 * @property {HTMLAnchorElement} [close] - the panel's first row (a.pods-switcher__current) — the
 *   current collection's own link; clicking it just collapses the panel. Optional.
 *
 * @extends {Component<Refs>}
 */
export class PodsSwitcher extends Component {
  // Per-instance element-listener guard — mirrors the old root.dataset.podsSwitcherReady: the
  // toggle/close listeners bind exactly once per node (element listeners survive a
  // disconnect/reconnect of the SAME node) and die with it when a re-render replaces it.
  #bound = false;

  // Aborts the per-instance document dismiss listeners on disconnect (the old module leaked these
  // globally by design; the port scopes them to the instance lifecycle).
  #dismiss = null;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs (synchronous) + wires the base MutationObserver

    this.#bindElementListeners();

    // Document-level dismissal — re-armed on every (re)connect, aborted on disconnect. BUBBLE
    // phase, no capture/options beyond the abort signal — same phase as the old global listeners.
    this.#dismiss?.abort(); // defensive: a double-connect without a disconnect must not double-bind
    this.#dismiss = new AbortController();
    const { signal } = this.#dismiss;

    // Outside-click dismissal. The toggle's own click handler runs first (target phase within this
    // root), so by the time this bubble-phase listener fires the root contains the target → no
    // self-close; a click on switcher B still closes an open switcher A (A's listener fires too).
    document.addEventListener(
      'click',
      (event) => {
        if (!this.classList.contains(OPEN_CLASS)) return;
        if (!this.contains(event.target)) this.#setOpen(false);
      },
      { signal }
    );

    // Escape closes and returns focus to the toggle (focus-return is part of the contract).
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        if (!this.classList.contains(OPEN_CLASS)) return;
        this.#setOpen(false);
        const toggle = this.refs.toggle;
        if (toggle) toggle.focus();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback(); // the base class disconnects its MutationObserver here
    this.#dismiss?.abort();
    this.#dismiss = null;
  }

  // Element-scoped handlers — bound once per instance; parity with the old controlSwitcher().
  #bindElementListeners() {
    if (this.#bound) return;
    this.#bound = true; // set BEFORE the existence check — a malformed root never rebinds (parity)

    const { toggle, panel, close } = this.refs;
    if (!toggle || !panel) return; // silent return — the old module never threw on malformed roots

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      this.#setOpen(!this.classList.contains(OPEN_CLASS));
    });

    if (close) {
      close.addEventListener('click', (event) => {
        // UNCONDITIONAL preventDefault — swallows cmd/ctrl-click too, exactly like the old module.
        event.preventDefault();
        this.#setOpen(false);
      });
      // The close row is an <a> (2026-07-03 spec rewrite): Enter fires its click (handled above),
      // but anchors IGNORE Space — the old <button> closed on it. Restore button-equivalent
      // keyboard dismissal: swallow the page scroll and collapse the panel.
      close.addEventListener('keydown', (event) => {
        if (event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        this.#setOpen(false);
      });
    }
  }

  // Exact old setOpen — synchronous, no rAF/timers: (1) is-open class on the root →
  // (2) aria-expanded on the toggle → (3) panel.hidden. Both is-open and hidden flip together.
  #setOpen(open) {
    const { toggle, panel } = this.refs;
    this.classList.toggle(OPEN_CLASS, open);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
  }
}

if (!customElements.get('pods-switcher')) {
  customElements.define('pods-switcher', PodsSwitcher);
}
